'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import { PrismicRichText } from '@prismicio/react';
import { asHTML, asText, Content } from '@prismicio/client';
import { ImQuotesLeft } from 'react-icons/im';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { FaInfoCircle } from 'react-icons/fa';

type ReviewsSliderDocument = Content.ReviewsSliderDocument;

interface ReviewsSliderProps {
  data: ReviewsSliderDocument['data'][];
  migrationNotice?: string | null;
}

const vignetteClass =
  "after:content-[''] after:absolute after:inset-0 after:z-1 after:pointer-events-none sm:border-b sm:border-[#444] after:bg-blue-700 after:opacity-90 lg:after:opacity-60";

export default function LongReviewsSlider({ data, migrationNotice }: ReviewsSliderProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [prevSlide, setPrevSlide] = useState<number | null>(null);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const reviews = data.flatMap((item) => item.review || []);
  const sliderRef = useRef<HTMLDivElement>(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const goToSlide = useCallback(
    (index: number) => {
      setPrevSlide(currentSlide);
      const newIndex = ((index % reviews.length) + reviews.length) % reviews.length;
      setCurrentSlide(newIndex);
    },
    [currentSlide, reviews.length]
  );

  const goToNextSlide = useCallback(() => {
    goToSlide(currentSlide + 1);
  }, [currentSlide, goToSlide]);

  const goToPrevSlide = useCallback(() => {
    goToSlide(currentSlide - 1);
  }, [currentSlide, goToSlide]);

  useEffect(() => {
    if (reviews.length === 0) return;

    const timer = setInterval(() => {
      goToNextSlide();
    }, 4000);

    return () => clearInterval(timer);
  }, [goToNextSlide, reviews.length]);

  // Touch event handlers for mobile swipe
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(0);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNextSlide();
    }
    if (isRightSwipe) {
      goToPrevSlide();
    }
  };

  if (reviews.length === 0) {
    return <div className="p-8 text-center">No reviews available.</div>;
  }

  return (
    <div
      className="relative w-full overflow-hidden h-[600px]"
      ref={sliderRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Gradient overlay */}
      <div className="absolute top-0 z-20 hidden h-[100px] w-full bg-gradient-to-b from-blue-900 to-transparent lg:h-[260px]"></div>

      {migrationNotice && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-30 px-8 pt-[4.5rem] sm:pt-16 lg:pt-20">
          <div className="lg:container">
            <div className="flex max-w-[520px] items-center gap-2 text-left text-sm italic text-white opacity-60 sm:pl-8">
              <FaInfoCircle className="shrink-0" aria-hidden="true" />
              <span>{migrationNotice}</span>
            </div>
          </div>
        </div>
      )}

      {/* Left and right navigation arrows */}
      <button
        className="absolute left-8 top-1/2 z-30 hidden -translate-y-1/2 rounded-full bg-black bg-opacity-30 p-2 text-white opacity-70 transition-opacity hover:opacity-100 focus:outline-none lg:block"
        onClick={goToPrevSlide}
        aria-label="Previous slide"
      >
        <FiChevronLeft size={24} />
      </button>

      <button
        className="absolute right-8 top-1/2 z-30 hidden -translate-y-1/2 rounded-full bg-black bg-opacity-30 p-2 text-white opacity-70 transition-opacity hover:opacity-100 focus:outline-none lg:block"
        onClick={goToNextSlide}
        aria-label="Next slide"
      >
        <FiChevronRight size={24} />
      </button>

      {/* Dot indicators */}
      <div className="absolute left-1/2 top-10 z-30 flex -translate-x-1/2 space-x-2 lg:bottom-12 lg:top-auto">
        {reviews.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2 w-2 rounded-full transition-all focus:outline-none ${
              index === currentSlide ? 'w-4 bg-white' : 'bg-white bg-opacity-50'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Review slides */}
      <AnimatePresence>
        {(
          [prevSlide, currentSlide].filter(
            (v, i, arr) => v !== null && arr.indexOf(v) === i
          ) as number[]
        ).map((slideIndex) => (
          <motion.div
            key={slideIndex}
            className={'absolute inset-0 z-10 flex items-end p-8'}
            initial={{ opacity: slideIndex === currentSlide ? 0 : 1 }}
            animate={{ opacity: slideIndex === currentSlide ? 1 : 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          >
            <div className={`absolute inset-0 ${vignetteClass}`}>
              {reviews[slideIndex].image.url && (
                <Image
                  src={reviews[slideIndex].wide_image.url || ''}
                  alt={reviews[slideIndex].wide_image.alt || ''}
                  fill
                  sizes="100vw"
                  className="object-cover object-center grayscale filter lg:object-left"
                  priority={slideIndex === 0}
                />
              )}
            </div>
            <div className="flex h-full lg:container">
              <div
                className={`relative z-10 mb-2 flex flex-col justify-end text-white sm:mb-8 sm:pl-8 lg:justify-center ${
                  asText(reviews[slideIndex].review).length > 200 ? 'max-w-4xl' : 'max-w-3xl'
                }`}
              >
                <ImQuotesLeft className="relative top-8 text-[4.5em] opacity-20" />
                <blockquote className="mb-6 text-xl font-normal leading-tight opacity-90 sm:opacity-100 lg:text-3xl">
                  <PrismicRichText field={reviews[slideIndex].review} />
                </blockquote>
                <div
                  dangerouslySetInnerHTML={{ __html: asHTML(reviews[slideIndex].name) }}
                  className="mb-1 text-base font-black uppercase sm:text-xl sm:font-normal"
                ></div>
                <p className="text-sm italic opacity-50 sm:text-base">
                  {reviews[slideIndex].position}
                </p>
                <p className="text-sm italic opacity-50 sm:text-base">
                  {reviews[slideIndex].company}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
